import { useSuspenseQuery } from "@apollo/client";
import { BreadcrumbArray, DataTable, PrimaryFullScreenSpinner } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { X } from "lucide-react";
import { Suspense, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { EburyTradeRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/ebury_/$clientId/trades")({
  head: () => opsHead("Ebury Client Trades"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const tradesDocument = graphql(`
  query AdminEburyClientTrades($id: ID!) {
    eburyClient(id: $id) {
      id
      clientName
      trades {
        ...EburyTradeRow
      }
    }
  }
`);

type TradeRow = EburyTradeRowFragment;

const columns: ColumnDef<TradeRow>[] = [
  {
    accessorKey: "id",
    header: "Trade ID",
    enableSorting: false,
  },
  {
    accessorKey: "tradeType",
    header: "Type",
    enableSorting: false,
  },
  {
    accessorKey: "orderDate",
    header: "Order Date",
    enableSorting: false,
  },
  {
    accessorKey: "maturityDate",
    header: "Maturity Date",
    cell: ({ row }) => new Date(row.original.maturityDate).toLocaleDateString(),
    enableSorting: false,
  },
  {
    id: "sell",
    header: "Sell",
    cell: ({ row }) =>
      `${row.original.sellAmount} ${row.original.sellCurrency}`,
    enableSorting: false,
  },
  {
    id: "buy",
    header: "Buy",
    cell: ({ row }) => `${row.original.buyAmount} ${row.original.buyCurrency}`,
    enableSorting: false,
  },
  {
    accessorKey: "rate",
    header: "Rate",
    cell: ({ row }) =>
      `${row.original.rate} ${row.original.rateSymbol ?? ""}`.trim(),
    enableSorting: false,
  },
  {
    accessorKey: "reference",
    header: "Reference",
    cell: ({ row }) => row.original.reference ?? "-",
    enableSorting: false,
  },
  {
    id: "transactionCount",
    header: "Transaction #",
    cell: ({ row }) => (row.original.transactions ?? []).length,
    enableSorting: false,
  },
];

const compactColumnVisibility = {
  orderDate: false,
  maturityDate: false,
  rate: false,
  reference: false,
};

function View() {
  const { clientId } = Route.useParams();
  const [selectedTrade, setSelectedTrade] = useState<TradeRow | null>(null);

  const { data } = useSuspenseQuery(tradesDocument, {
    variables: { id: clientId },
  });

  const client = data.eburyClient;
  const trades = client?.trades ?? [];

  return (
    <AdminPageContainer testId="admin-ebury-client-trades">
      <BreadcrumbArray
        stack={[
          { to: "/ebury", title: "Ebury Clients" },
          { title: client?.clientName ?? clientId },
        ]}
      />
      <AdminPageHeader
        title={`${client?.clientName ?? clientId} — Trades`}
        subtitle={clientId}
      />
      <div className="flex min-h-0 gap-4">
        <div className="min-w-0 flex-1">
          <DataTable
            data={trades}
            columns={columns}
            columnVisibility={
              selectedTrade ? compactColumnVisibility : undefined
            }
            activeRowId={selectedTrade?.id}
            onRowClick={(row) => {
              setSelectedTrade(
                row.original.id === selectedTrade?.id ? null : row.original,
              );
            }}
            className="overflow-hidden border"
          />
        </div>
        {selectedTrade && (
          <TradeDetailPane
            trade={selectedTrade}
            onClose={() => setSelectedTrade(null)}
          />
        )}
      </div>
    </AdminPageContainer>
  );
}

function TradeDetailPane({
  trade,
  onClose,
}: {
  trade: TradeRow;
  onClose: () => void;
}) {
  return (
    <div className="w-1/2 flex-shrink-0 overflow-auto border-l px-6 py-2">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold text-lg">Trade Detail</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-secondary"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="text-sm">
        <DetailRow label="ID" value={trade.id} />
        <DetailRow label="Type" value={trade.tradeType} />
        <DetailRow label="Status" value={trade.status} />
        <DetailRow label="Order Date" value={trade.orderDate} />
        <DetailRow
          label="Maturity Date"
          value={new Date(trade.maturityDate).toLocaleDateString()}
        />
        <DetailRow
          label="Sell"
          value={`${trade.sellAmount} ${trade.sellCurrency}`}
        />
        <DetailRow
          label="Buy"
          value={`${trade.buyAmount} ${trade.buyCurrency}`}
        />
        <DetailRow
          label="Rate"
          value={`${trade.rate} ${trade.rateSymbol}`.trim()}
        />
        <DetailRow
          label="Fee"
          value={`${trade.feeAmount.amount} ${trade.feeAmount.currency}`}
        />
        <DetailRow label="Synthetic" value={trade.synthetic ? "Yes" : "No"} />
        <DetailRow label="Parent Trade" value={trade.parentTradeId ?? "-"} />
        <DetailRow label="Reference" value={trade.reference ?? "-"} />
        <DetailRow label="Client ID" value={trade.eburyClientId} />

        {(trade.__typename === "EburyFixedForwardDetails" ||
          trade.__typename === "EburyWindowForwardDetails") && (
          <>
            <DetailRow
              label="Buy Balance"
              value={`${trade.buyBalanceForDrawdown.amount} ${trade.buyBalanceForDrawdown.currency}`}
            />
            <DetailRow
              label="Sell Balance"
              value={`${trade.sellBalanceForDrawdown.amount} ${trade.sellBalanceForDrawdown.currency}`}
            />
            <DetailRow
              label="Initial Margin"
              value={`${trade.initialMargin.amount} ${trade.initialMargin.currency}`}
            />
            <DetailRow
              label="Margin Paid"
              value={(() => {
                if (trade.initialMarginPaid === null) {
                  return "-";
                }
                return trade.initialMarginPaid ? "Yes" : "No";
              })()}
            />
            <DetailRow
              label="Margin Call"
              value={`${trade.marginCall.amount} ${trade.marginCall.currency}`}
            />
          </>
        )}

        {(trade.transactions ?? []).length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 font-semibold text-sm">Transactions</h3>
            <div className="flex flex-col gap-3">
              {(trade.transactions ?? []).map((txn) => (
                <div key={txn.transactionId} className="rounded border p-3">
                  <DetailRow label="ID" value={txn.transactionId} />
                  <DetailRow
                    label="Amount"
                    value={`${txn.creditDebitIndicator === "CRDT" ? "+" : "-"}${txn.amount.amount} ${txn.amount.currency}`}
                  />
                  <DetailRow label="Type" value={txn.transactionType ?? "-"} />
                  <DetailRow label="Status" value={txn.status} />
                  <DetailRow
                    label="Reference"
                    value={txn.transactionReference ?? "-"}
                  />
                  <DetailRow
                    label="Info"
                    value={txn.transactionInformation ?? "-"}
                  />
                  <DetailRow
                    label="Additional Info"
                    value={txn.additionalTransactionInformation ?? "-"}
                  />
                  <DetailRow
                    label="Booking Date"
                    value={new Date(txn.bookingDatetime).toLocaleString()}
                  />
                  <DetailRow
                    label="Value Date"
                    value={new Date(txn.valueDatetime).toLocaleString()}
                  />
                  <DetailRow label="Account ID" value={txn.accountId} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
